/**
 * Subscription service (web).
 *
 * Two sources of truth, used for different jobs:
 *   • RevenueCat customer info drives immediate UI updates after a purchase.
 *   • The ChordBook Pro API is authoritative for what the user is actually allowed
 *     to do. Every protected mutation is checked server-side regardless of
 *     what this module reports.
 */

import { PLAN, SUBSCRIPTION_STATUS } from '@chordbookpro/shared';
import { api } from '../api.js';
import * as client from './revenueCatWebClient.js';
import { SubscriptionError, translatePurchaseError } from './subscriptionErrors.js';

/** Signs the user in to RevenueCat with their Firebase UID. */
export async function initializeForUser(firebaseUid) {
  if (!client.isConfigured()) return { configured: false };

  try {
    await client.configure(firebaseUid);
    return { configured: true };
  } catch (error) {
    // Billing being unavailable must never block the app from loading.
    console.warn('RevenueCat could not be configured:', error);
    return { configured: false, error };
  }
}

/** Tears down billing state on sign-out. */
export async function resetForSignOut() {
  await client.reset();
}

/**
 * Loads the offerings and normalizes the packages into what the paywall needs.
 *
 * Prices, currencies, billing periods and trial eligibility all come from the
 * store — nothing is hardcoded.
 */
export async function loadOfferings() {
  const offerings = await client.getOfferings();
  if (!offerings) return { available: false, packages: [] };

  const current = offerings.current ?? null;
  if (!current) return { available: false, packages: [] };

  const packages = (current.availablePackages ?? []).map(normalizePackage);

  return {
    available: packages.length > 0,
    offeringId: current.identifier,
    packages,
    monthly: packages.find((item) => item.period === 'monthly') ?? null,
    annual: packages.find((item) => item.period === 'annual') ?? null,
  };
}

/**
 * Converts an SDK package into a display model.
 *
 * Trial eligibility is read from the package itself. We show trial wording
 * only when the store says this customer can actually have one — never as a
 * blanket marketing claim.
 */
function normalizePackage(rcPackage) {
  const product = rcPackage.webBillingProduct ?? rcPackage.rcBillingProduct ?? rcPackage.product ?? {};
  const price = product.currentPrice ?? product.price ?? {};

  const trialPhase =
    product.defaultSubscriptionOption?.trial ??
    product.defaultPurchaseOption?.trial ??
    product.trial ??
    null;

  const periodKey = normalizePeriod(
    product.normalPeriodDuration ?? product.defaultSubscriptionOption?.base?.period ?? '',
    rcPackage.packageType ?? rcPackage.identifier,
  );

  return {
    id: rcPackage.identifier,
    packageType: rcPackage.packageType ?? null,
    period: periodKey,
    productId: product.identifier ?? null,
    title: product.title ?? '',
    description: product.description ?? '',

    // Localized, store-provided strings — displayed verbatim.
    priceFormatted: price.formattedPrice ?? price.formatted ?? '',
    priceAmountMicros: price.amountMicros ?? null,
    currency: price.currency ?? null,

    // Trial wording is gated on this being non-null.
    trial: trialPhase
      ? {
          available: true,
          periodDuration: trialPhase.periodDuration ?? trialPhase.period ?? null,
          periodLabel: describeTrial(trialPhase),
        }
      : { available: false, periodDuration: null, periodLabel: null },

    raw: rcPackage,
  };
}

function normalizePeriod(isoDuration, fallbackType) {
  const value = String(isoDuration ?? '').toUpperCase();
  if (value.includes('Y') || value === 'P1Y') return 'annual';
  if (value === 'P1M' || value.includes('M')) return 'monthly';
  if (value.includes('W')) return 'weekly';

  const type = String(fallbackType ?? '').toLowerCase();
  if (type.includes('annual') || type.includes('year')) return 'annual';
  if (type.includes('month')) return 'monthly';
  return 'other';
}

/** Human phrase for a trial phase, derived from the store's own duration. */
function describeTrial(trialPhase) {
  const duration = String(trialPhase.periodDuration ?? trialPhase.period ?? '').toUpperCase();

  const match = /^P(\d+)([DWMY])$/.exec(duration);
  if (!match) return 'free trial';

  const amount = Number(match[1]);
  const unitNames = { D: 'day', W: 'week', M: 'month', Y: 'year' };
  const unit = unitNames[match[2]] ?? 'day';

  return `${amount}-${unit}${amount === 1 ? '' : 's'} free trial`.replace('-days', '-day');
}

/**
 * Purchases a package, then syncs the backend so entitlement is verified
 * server-side before any Premium-gated request is made.
 */
export async function purchase(displayPackage, { email } = {}) {
  if (!displayPackage?.raw) {
    throw new SubscriptionError('PURCHASE_NOT_FOUND', 'That subscription option is unavailable.');
  }

  try {
    const result = await client.purchasePackage(displayPackage.raw, { email });

    // Ask the server to re-read the entitlement from RevenueCat. Until this
    // succeeds the client may show Premium optimistically, but the API will not
    // honour Premium-only mutations.
    const status = await syncWithBackend('purchase');

    return { success: true, customerInfo: result?.customerInfo ?? null, status };
  } catch (error) {
    throw translatePurchaseError(error);
  }
}

/**
 * Asks the API to refresh its entitlement snapshot from RevenueCat.
 * The API remains authoritative; this is how the client makes it re-check.
 */
export async function syncWithBackend(reason = 'manual') {
  try {
    return await api.billing.sync({ reason });
  } catch (error) {
    // A failed sync must not downgrade the user — the previous snapshot stands.
    console.warn('Billing sync failed:', error);
    throw error;
  }
}

/** Reads the authoritative status from the API. */
export async function loadStatus() {
  return api.billing.status();
}

/** Reads customer info straight from the SDK, for immediate UI feedback. */
export async function loadCustomerInfo() {
  try {
    const info = await client.getCustomerInfo();
    if (!info) return null;

    return {
      isPro: client.hasProEntitlement(info),
      entitlement: client.getProEntitlement(info),
      managementURL: info.managementURL ?? null,
      raw: info,
    };
  } catch {
    return null;
  }
}

/**
 * Derives a display status from an entitlement.
 * Used only for immediate feedback; the API's value wins on reload.
 */
export function deriveClientStatus(customerInfo) {
  if (!customerInfo?.isPro) return { plan: PLAN.FREE, status: SUBSCRIPTION_STATUS.FREE };

  const entitlement = customerInfo.entitlement;
  const periodType = String(entitlement?.periodType ?? '').toLowerCase();

  if (periodType === 'trial') {
    return { plan: PLAN.PRO, status: SUBSCRIPTION_STATUS.TRIALING };
  }
  if (entitlement && entitlement.willRenew === false) {
    return { plan: PLAN.PRO, status: SUBSCRIPTION_STATUS.CANCELED_ACTIVE };
  }

  return { plan: PLAN.PRO, status: SUBSCRIPTION_STATUS.ACTIVE };
}

export { client };
